import { NavLink, useParams, useSearchParams } from 'react-router';
import { PageHero } from '@/components/layout/PageHero';
import { ArrowIcon, ButtonLink } from '@/components/ui/Button';
import { CatalogueButtons } from '@/components/ui/CatalogueButtons';
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback';
import { Pagination } from '@/components/ui/Misc';
import { DIVISION_PATH, useCategories, useProducts } from '@/features/catalog/api';
import { ProductCard, ProductGridSkeleton } from '@/features/catalog/ProductCard';
import { CATEGORY_PHOTOS } from '@/features/site/media';
import { Seo } from '@/features/site/Seo';
import { useSite } from '@/features/site/SiteProvider';
import { InquirySection, ProductionInfoStrip } from '@/features/site/sections';
import { cn } from '@/lib/utils';
import type { Division } from '@/types/api';
import NotFoundPage from './NotFoundPage';

const PAGE_SIZE = 12;

export default function CatalogPage({ division }: { division: Division }) {
  const { categorySlug } = useParams();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get('page')) || 1);
  const { content } = useSite();
  const intro = division === 'APPAREL' ? content.catalogIntro.apparel : content.catalogIntro.sports;
  const basePath = DIVISION_PATH[division];
  const isApparel = division === 'APPAREL';

  const categories = useCategories(division);
  const products = useProducts({ division, category: categorySlug, page, pageSize: PAGE_SIZE });
  const current = categories.data?.find((c) => c.slug === categorySlug);

  if (categorySlug && categories.isSuccess && !current) return <NotFoundPage />;

  const divisionName = isApparel ? 'Apparel' : 'Sports Goods';
  const title = current ? current.seoTitle || `${current.name} — ${divisionName}` : intro.heading;
  const description = current?.seoDescription || current?.description || intro.intro;

  return (
    <>
      <Seo title={title} description={description} path={categorySlug ? `${basePath}/${categorySlug}` : basePath} />

      <PageHero
        crumbs={current ? [{ label: divisionName, to: basePath }, { label: current.name }] : [{ label: divisionName }]}
        eyebrow={intro.eyebrow}
        title={current?.name ?? intro.heading}
        intro={current?.description ?? intro.intro}
        photo={(current && CATEGORY_PHOTOS[current.slug]) || (isApparel ? 'garmentWorkshop' : 'ballStitching')}
        actions={
          <>
            <ButtonLink to="#inquiry" variant="accent" size="lg" icon={<ArrowIcon />}>
              Request a Quote
            </ButtonLink>
            <CatalogueButtons />
          </>
        }
      />

      <nav aria-label={`${divisionName} categories`} className="sticky top-16 z-30 border-b border-line bg-white/95 backdrop-blur-md">
        <div className="container-page overflow-x-auto">
          {categories.isPending ? (
            <div className="flex gap-6 py-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-5 w-24" />
              ))}
            </div>
          ) : (
            <ul className="flex min-w-max gap-1">
              {[{ slug: '', name: 'All', productCount: undefined as number | undefined }, ...(categories.data ?? [])].map((c) => (
                <li key={c.slug || 'all'}>
                  <NavLink
                    to={c.slug ? `${basePath}/${c.slug}` : basePath}
                    end
                    className={({ isActive }) =>
                      cn(
                        'relative flex h-14 items-center gap-1.5 px-3 text-[14px] font-medium whitespace-nowrap transition-colors',
                        'after:absolute after:inset-x-3 after:bottom-0 after:h-[2px] after:bg-accent after:transition-transform',
                        isActive ? 'text-primary after:scale-x-100' : 'text-muted after:scale-x-0 hover:text-primary',
                      )
                    }
                  >
                    {c.name}
                    {c.productCount !== undefined && <span className="text-[12px] text-muted">{c.productCount}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          )}
        </div>
      </nav>

      <section className="py-14 lg:py-20" aria-label="Products">
        <div className="container-page">
          {products.isPending ? (
            <ProductGridSkeleton />
          ) : products.isError ? (
            <ErrorState error={products.error} onRetry={() => void products.refetch()} />
          ) : products.data.data.length === 0 ? (
            <EmptyState
              title="No products listed here yet"
              description="We manufacture to order — send us your requirements and we will confirm feasibility, MOQ and pricing."
              action={<ButtonLink to="#inquiry">Request a quote</ButtonLink>}
            />
          ) : (
            <>
              <p className="mb-8 text-[14px] text-muted" aria-live="polite">
                {products.data.meta.total} product{products.data.meta.total === 1 ? '' : 's'}
              </p>
              <div className={cn('grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3', products.isPlaceholderData && 'opacity-60')}>
                {products.data.data.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
              <div className="mt-8">
                <Pagination
                  page={page}
                  totalPages={products.data.meta.totalPages}
                  onChange={(p) => {
                    setParams(p > 1 ? { page: String(p) } : {});
                    window.scrollTo({ top: 0 });
                  }}
                />
              </div>
            </>
          )}
        </div>
      </section>

      <ProductionInfoStrip />
      <InquirySection
        context={{ categorySlug: current?.slug }}
        heading={current ? `Get a quote for ${current.name.toLowerCase()}` : `Get a quote for ${divisionName.toLowerCase()}`}
        formTitle="Request a Quote"
      />
    </>
  );
}
